import {
  keepPreviousData,
  queryOptions,
  useQuery,
} from "@tanstack/react-query";
import api from "@/core/api/axios";
import { useAuth } from "@/core/hooks/useAuth";
import { useUserSector } from "@/core/hooks/useUserSector";
import type {
  AccessProfile,
  BFFUser,
  GlobalPermission,
} from "@superagents/types/api";
import type { APIResponseBFF } from "@superagents/types/api/api.v5";

export type SectorAdmin = NonNullable<BFFUser["sectorAdmins"]>[number];
/** Área de que o usuário é membro no setor ativo (só id e nome). */
export type MyArea = NonNullable<BFFUser["areas"]>[number];

type SectorPermissionsData = {
  permissions: GlobalPermission[];
  profile: AccessProfile | null;
  sectorAdmins: SectorAdmin[];
  areas: MyArea[];
  /** Derivado de `areas` uma vez por resposta: referência estável. */
  areaIds: string[];
  sectorHasAreas: boolean;
};

// Referências estáveis: quem consome (Navbar, gates) não re-renderiza por
// ganhar um `[]` novo a cada render enquanto a query não tem dado.
const NO_PERMISSIONS: GlobalPermission[] = [];
const NO_ADMINS: SectorAdmin[] = [];
const NO_AREAS: MyArea[] = [];
const NO_AREA_IDS: string[] = [];

/**
 * `/user/me?sectorId=` do setor ativo, para o hook e para o boot (SA-1452).
 * `sectorId` nulo pede o usuário sem setor (mesma chave de antes).
 */
export const sectorPermissionsQueryOptions = (
  sectorId: string | null,
  token: string | undefined
) =>
  queryOptions({
    queryKey: ["user", "me", "permissions", sectorId],
    queryFn: async (): Promise<SectorPermissionsData> => {
      const response = await api().get<APIResponseBFF<"bff::user">>(
        "/user/me",
        {
          headers: { Authorization: `Bearer ${token}` },
          params: sectorId ? { sectorId } : undefined,
        }
      );
      const user = response.data?.data;
      const areas = user?.areas ?? NO_AREAS;
      return {
        permissions: user?.permissions ?? NO_PERMISSIONS,
        profile: user?.profile ?? null,
        sectorAdmins: user?.sectorAdmins ?? NO_ADMINS,
        areas,
        areaIds: areas.length
          ? areas.map((area) => area.documentId)
          : NO_AREA_IDS,
        sectorHasAreas: user?.sectorHasAreas ?? false,
      };
    },
    staleTime: 1000 * 60 * 5,
  });

/**
 * Permissões, perfil e administradores do SETOR ATIVO.
 *
 * `user.permissions` (do AuthProvider) reflete apenas o primeiro account_access
 * ativo e não é refeito ao trocar de setor — o que esconde gates em setores
 * onde o usuário é admin mas seu primeiro acesso é member. Aqui buscamos
 * `/user/me?sectorId=<ativo>`, que o backend resolve via `requireUserAccess`
 * para o account_access daquele setor, retornando as permissões corretas.
 *
 * `keepPreviousData`: ao trocar de setor, o setor anterior segue valendo até a
 * resposta nova chegar, então os gates não piscam `Loading`. `isLoading` só é
 * true no primeiro carregamento. A queryKey começa por
 * `["user","me","permissions"]`: é o prefixo que o interceptor de 403 invalida.
 *
 * `areas`/`areaIds`/`sectorHasAreas` (SA-1367): minhas áreas no setor ativo e
 * se ele tem alguma área. Recortam a lista de conversas e o menu da conta; o
 * membro comum não pode listar `/areas`, então elas vêm daqui.
 *
 * `isError` (SA-1360): a busca do setor ATIVO falhou (depois dos retries) e
 * não há dado dele. O dado do setor anterior não conta: o TanStack só aplica o
 * placeholder (keepPreviousData) enquanto a query nova está `pending`, então
 * ele some quando o setor novo falha e VOLTA no retry. Aplicar as permissões
 * de outro setor montaria páginas que respondem 403. Por isso o erro vale até
 * uma resposta do setor ativo chegar, inclusive durante o retry (`isFetching`),
 * e nesse estado `permissions`/`sectorAdmins`/`areas` voltam vazios, `profile`
 * nulo e `sectorHasAreas` falso.
 * Quem decide bloqueio ou aviso de administrador checa `isError` ANTES de
 * `isLoading` e oferece `refetch`. Falha num refetch com dado do próprio setor
 * já carregado não conta: o dado segue valendo.
 */
export const useSectorPermissions = () => {
  const { token, isLoggedIn } = useAuth();
  const { activeSectorId } = useUserSector();

  const query = useQuery({
    ...sectorPermissionsQueryOptions(activeSectorId ?? null, token),
    enabled: isLoggedIn && !!token,
    placeholderData: keepPreviousData,
  });

  // `errorUpdatedAt` fica > 0 depois da primeira falha desta queryKey e não é
  // zerado pelo retry: com ele, o erro sobrevive ao `pending` do retry e ao
  // placeholder do setor anterior. Some quando chega dado do próprio setor.
  const isError =
    query.errorUpdatedAt > 0 &&
    (query.data === undefined || query.isPlaceholderData);
  const data = isError ? undefined : query.data;

  return {
    permissions: data?.permissions ?? NO_PERMISSIONS,
    profile: data?.profile ?? null,
    sectorAdmins: data?.sectorAdmins ?? NO_ADMINS,
    areas: data?.areas ?? NO_AREAS,
    areaIds: data?.areaIds ?? NO_AREA_IDS,
    sectorHasAreas: data?.sectorHasAreas ?? false,
    isLoading: query.isLoading,
    // Dado de OUTRO setor (keepPreviousData) enquanto o do ativo não chega, na
    // 1ª visita a um setor. Serve ao menu (não pisca); quem decide se a página
    // monta espera (review da PR #852: montar com as permissões do setor
    // anterior disparava as rotas do setor novo, que respondiam 403).
    isPlaceholder: query.isPlaceholderData && !isError,
    isError,
    isFetching: query.isFetching,
    refetch: query.refetch,
  };
};
